import { useEffect, useRef, useState } from "react";
import {
  GuestPhoto,
  CountdownSection,
  GallerySection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import type { VariantProps } from "./types";

// ═══════════════════════════════════════════════════════════════════════
// 6. CELEBRATION — birthday: confetti, playful, wishlist
//
// Design notes (Confetti / b02 — kid's birthday):
//   * Animated confetti drift in the hero — 40 pieces, varied shapes &
//     colors, each on a unique keyframe path.
//   * Big "7" centerpiece with hand-drawn circle, candles on top, sparkles.
//   * "Click to celebrate" button fires a CSS-only confetti burst from the
//     click position — pure delight, kid-pleasing.
//   * Polaroid scatter lifts on hover (transform + shadow grow) and plays
//     a subtle `tap` sound.
//   * Wishlist redrawn as gift tags — twine string, hole punch, claim
//     button that pops + flips to "Claimed!".
//   * Section reveals use bounce easing (cubic-bezier overshoot, 500ms).
//   * `prefers-reduced-motion: reduce` disables all animation; static
//     equivalents render in place.
// ═══════════════════════════════════════════════════════════════════════

// ─── Confetti palette ───────────────────────────────────────────────────
const CONFETTI_COLORS = ["#C8804B", "#7A8268", "#C9A24E", "#8B5C7E", "#2A1F18"];

type ConfettiShape = "rect" | "circle" | "tri" | "squiggle";
const SHAPES: ConfettiShape[] = ["rect", "circle", "tri", "squiggle"];

// One drifting piece for the hero ambient confetti.
interface DriftPiece {
  id: number;
  left: number; // %
  top: number; // %
  size: number;
  rot: number;
  delay: number;
  duration: number;
  driftX: number; // px
  color: string;
  shape: ConfettiShape;
  variant: number; // which keyframe to use (0..3)
}

function buildDrift(n: number): DriftPiece[] {
  // Deterministic so SSR/client agree.
  const rng = (seed: number) => {
    const x = Math.sin(seed * 9301 + 49297) * 233280;
    return x - Math.floor(x);
  };
  return Array.from({ length: n }).map((_, i) => ({
    id: i,
    left: rng(i + 1) * 100,
    top: rng(i + 100) * 100,
    size: 5 + rng(i + 200) * 9,
    rot: rng(i + 300) * 360,
    delay: -rng(i + 400) * 8,
    duration: 7 + rng(i + 500) * 6,
    driftX: (rng(i + 600) - 0.5) * 60,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    shape: SHAPES[i % SHAPES.length],
  variant: i % 4,
  }));
}

// Renders the right shape for a confetti piece.
function ConfettiShapeEl({
  shape,
  size,
  color,
}: {
  shape: ConfettiShape;
  size: number;
  color: string;
}) {
  switch (shape) {
    case "circle":
      return (
        <span
          style={{
            display: "block",
            width: size,
            height: size,
            borderRadius: "50%",
            background: color,
          }}
        />
      );
    case "rect":
      return (
        <span
          style={{
            display: "block",
            width: size + 2,
            height: size * 0.45,
            background: color,
          }}
        />
      );
    case "tri":
      return (
        <span
          style={{
            display: "block",
            width: 0,
            height: 0,
            borderLeft: `${size / 2}px solid transparent`,
            borderRight: `${size / 2}px solid transparent`,
            borderBottom: `${size}px solid ${color}`,
          }}
        />
      );
    case "squiggle":
      return (
        <svg width={size + 6} height={6} viewBox="0 0 18 6">
          <path
            d="M0 3 Q 3 0 6 3 T 12 3 T 18 3"
            stroke={color}
            strokeWidth="1.6"
            fill="none"
          />
        </svg>
      );
  }
}

// ─── Burst (signature: click-to-celebrate) ─────────────────────────────
interface BurstPiece {
  id: number;
  dx: number;
  dy: number;
  rot: number;
  size: number;
  color: string;
  shape: ConfettiShape;
}
interface Burst {
  id: number;
  x: number;
  y: number;
  pieces: BurstPiece[];
}

let burstSeq = 0;
function makeBurst(x: number, y: number): Burst {
  const pieces: BurstPiece[] = Array.from({ length: 22 }).map((_, i) => {
    const angle = (i / 22) * Math.PI * 2 + Math.random() * 0.4;
    const speed = 80 + Math.random() * 140;
    return {
      id: i,
      dx: Math.cos(angle) * speed,
      dy: Math.sin(angle) * speed - 40, // bias upward
      rot: (Math.random() - 0.5) * 720,
      size: 6 + Math.random() * 8,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      shape: SHAPES[i % SHAPES.length],
    };
  });
  return { id: ++burstSeq, x, y, pieces };
}

// ─── Section reveal hook (IntersectionObserver) ────────────────────────
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setShown(true);
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, shown };
}

// Reveal-style helper — bounce overshoot transform on enter.
function revealStyle(shown: boolean): React.CSSProperties {
  return {
    opacity: shown ? 1 : 0,
    transform: shown ? "translateY(0)" : "translateY(28px)",
    transition:
      "opacity 500ms ease, transform 500ms cubic-bezier(0.34, 1.56, 0.64, 1)",
  };
}

// ─── Main variant ───────────────────────────────────────────────────────
export function VariantCelebration({ t, isDark, ctx }: VariantProps) {
  const p = t.photos || [];
  const { play } = useSound();
  const tt = useT();

  // The full drift array is built once per mount. 40 pieces.
  const driftRef = useRef<DriftPiece[]>(buildDrift(40));
  const drift = driftRef.current;

  const [bursts, setBursts] = useState<Burst[]>([]);
  const [claimed, setClaimed] = useState<Record<number, boolean>>({});
  const heroRef = useRef<HTMLDivElement | null>(null);

  const polaroids = useReveal<HTMLDivElement>();
  const cake = useReveal<HTMLDivElement>();
  const wishlist = useReveal<HTMLDivElement>();

  // Detect reduced motion at mount.
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Auto-clean burst after animation completes (1s window).
  useEffect(() => {
    if (!bursts.length) return;
    const timers = bursts.map((b) =>
      window.setTimeout(() => {
        setBursts((bs) => bs.filter((x) => x.id !== b.id));
      }, 1100),
    );
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [bursts]);

  function fireBurst(e: React.MouseEvent<HTMLButtonElement>) {
    play("chime");
    play("pop");
    if (reduceMotion) return; // sound only — no animated particles
    const host = heroRef.current;
    if (!host) return;
    const hostRect = host.getBoundingClientRect();
    const btnRect = e.currentTarget.getBoundingClientRect();
    const x = btnRect.left + btnRect.width / 2 - hostRect.left;
    const y = btnRect.top + btnRect.height / 2 - hostRect.top;
    setBursts((bs) => [...bs, makeBurst(x, y)]);
  }

  // RSVP wrapper: play chime+pop on submit (yes path).
  const wrappedSetRsvp = (v: string | null) => {
    if (v === "yes") {
      play("chime");
      window.setTimeout(() => play("pop"), 140);
    } else if (v === "no") {
      play("tap");
    }
    ctx.rsvp.setRsvp(v);
  };

  function claim(i: number) {
    if (claimed[i]) return;
    play("pop");
    setClaimed((c) => ({ ...c, [i]: true }));
  }

  // ─── Static mobile confetti scatter (60 pieces, deterministic) ──────
  // Built once per mount. Used only on mobile (≤768px) — desktop keeps
  // the animated drift layer above. Variety in shape + 5 colors gives
  // the page a denser, joyful party-paper feel without any motion.
  const mobileConfettiRef = useRef(
    (() => {
      const rng = (seed: number) => {
        const x = Math.sin(seed * 9301 + 49297) * 233280;
        return x - Math.floor(x);
      };
      return Array.from({ length: 60 }).map((_, i) => ({
        id: i,
        left: rng(i + 1) * 100,
        top: rng(i + 100) * 100,
        size: 5 + rng(i + 200) * 9,
        rot: rng(i + 300) * 360,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        shape: SHAPES[i % SHAPES.length],
      }));
    })(),
  );
  const mobileConfetti = mobileConfettiRef.current;

  return (
    <div className="confetti-variant">
      {/* ─── Stylesheet for animations + bounce + burst keyframes ───── */}
      <style>{`
        @keyframes confettiDrift0 {
          0%   { transform: translate3d(0,0,0) rotate(0deg); }
          50%  { transform: translate3d(20px,30px,0) rotate(180deg); }
          100% { transform: translate3d(-10px,60px,0) rotate(360deg); }
        }
        @keyframes confettiDrift1 {
          0%   { transform: translate3d(0,-10px,0) rotate(0deg); }
          50%  { transform: translate3d(-25px,20px,0) rotate(-160deg); }
          100% { transform: translate3d(15px,50px,0) rotate(-340deg); }
        }
        @keyframes confettiDrift2 {
          0%   { transform: translate3d(0,0,0) rotate(45deg); }
          33%  { transform: translate3d(15px,-12px,0) rotate(160deg); }
          66%  { transform: translate3d(-12px,18px,0) rotate(280deg); }
          100% { transform: translate3d(8px,40px,0) rotate(420deg); }
        }
        @keyframes confettiDrift3 {
          0%   { transform: translate3d(0,5px,0) rotate(0deg); }
          50%  { transform: translate3d(30px,-20px,0) rotate(220deg); }
          100% { transform: translate3d(-5px,30px,0) rotate(440deg); }
        }
        @keyframes flameFlicker {
          0%, 100% { transform: scale(1) rotate(-1deg); opacity: 0.95; }
          50%      { transform: scale(1.15) rotate(2deg); opacity: 1; }
        }
        @keyframes sparkleTwinkle {
          0%, 100% { opacity: 0.35; transform: scale(0.8) rotate(0deg); }
          50%      { opacity: 1;    transform: scale(1.15) rotate(45deg); }
        }
        @keyframes burstFly {
          0%   { transform: translate3d(0,0,0) rotate(0deg) scale(1); opacity: 1; }
          100% { transform: translate3d(var(--bx), var(--by), 0) rotate(var(--brot)) scale(0.6); opacity: 0; }
        }
        @keyframes confettiStaggerIn {
          0%   { opacity: 0; transform: translate3d(0,-12px,0) rotate(0deg); }
          100% { opacity: 0.7; transform: translate3d(0,0,0) rotate(var(--rot,0deg)); }
        }
        @media (prefers-reduced-motion: reduce) {
          .b02-drift, .b02-flame, .b02-sparkle, .b02-stagger { animation: none !important; }
        }
        .confetti-variant button:focus-visible,
        .confetti-variant a:focus-visible {
          outline: 2px solid var(--b02-ink, #2A1F18);
          outline-offset: 3px;
        }

        /* ═══════════════════════════════════════════════════════════════
           MOBILE OVERRIDES (≤768px)
           Lockdown silences animation on mobile, so we lean on STATIC
           visual joy — bigger numerals, denser confetti scatter, vertical
           polaroid reel, full-width gift tags, chunky party RSVP button.
           ═══════════════════════════════════════════════════════════════ */
        @media (max-width: 768px) {
          /* All ambient & flicker animations off — joy comes from color/scale */
          .confetti-variant .b02-drift,
          .confetti-variant .b02-flame,
          .confetti-variant .b02-sparkle,
          .confetti-variant .b02-stagger { animation: none !important; }

          /* Hide the desktop drift layer entirely; mobile uses denser static layer */
          .confetti-variant .b02-mobile-hide-drift { display: none !important; }
          /* Show static mobile confetti scatter only on mobile */
          .confetti-variant .b02-mobile-confetti { display: block !important; }

          /* HERO — tighter padding, more breathing for the giant 7 */
          .confetti-variant .b02-hero {
            padding: 56px 20px 88px !important;
            min-height: 100vh !important;
          }

          /* "happy birthday" handwritten — bigger, terracotta, sparkle accents */
          .confetti-variant .b02-hand-callout {
            font-size: clamp(40px, 10vw, 64px) !important;
            margin-bottom: 8px !important;
          }

          /* Names — keep readable below the giant numeral */
          .confetti-variant .b02-names {
            font-size: clamp(40px, 9vw, 64px) !important;
            margin-bottom: 8px !important;
          }

          /* The MASSIVE "7" — fills almost half the viewport height */
          .confetti-variant .b02-seven {
            font-size: clamp(120px, 30vw, 200px) !important;
          }
          .confetti-variant .b02-seven-wrap {
            margin: 4px auto 18px !important;
            padding: 18px 32px !important;
          }

          /* "yosh bo'ladi!" italic line — bigger, hand-feel */
          .confetti-variant .b02-yosh {
            font-size: clamp(28px, 7vw, 40px) !important;
            margin-bottom: 24px !important;
          }

          /* Hero portrait — keep round but trim the ring */
          .confetti-variant .b02-hero-portrait {
            max-width: 220px !important;
            border-width: 6px !important;
            margin: 0 auto 22px !important;
          }

          /* Date / venue line */
          .confetti-variant .b02-datevenue {
            font-size: 18px !important;
            margin-bottom: 22px !important;
          }

          /* Party-style RSVP button — chunky 56px tap target with 3px shadow */
          .confetti-variant .b02-cta {
            display: block !important;
            width: 100% !important;
            min-height: 56px !important;
            padding: 16px 20px !important;
            font-size: 15px !important;
            letter-spacing: 0.14em !important;
            box-shadow: 3px 3px 0 0 var(--b02-ink, #2A1F18) !important;
            border-radius: 6px !important;
          }

          /* Static party-favor row — visible only on mobile */
          .confetti-variant .b02-favors {
            display: flex !important;
            justify-content: space-between !important;
            align-items: flex-end !important;
            gap: 6px !important;
            margin-top: 32px !important;
            padding: 0 4px !important;
          }
          .confetti-variant .b02-favors > * {
            flex: 1 1 0 !important;
            display: flex !important;
            justify-content: center !important;
          }

          /* Section headings — a touch tighter on mobile */
          .confetti-variant .b02-h2 {
            font-size: 36px !important;
          }
          .confetti-variant .b02-section {
            padding: 64px 20px !important;
          }

          /* Polaroid scatter — vertical reel, stack with alternating tilt */
          .confetti-variant .b02-polaroid-row {
            flex-direction: column !important;
            align-items: center !important;
            gap: 32px !important;
          }
          .confetti-variant .b02-polaroid {
            width: min(280px, 86vw) !important;
            transform: rotate(var(--b02-mobile-tilt, 0deg)) !important;
          }

          /* Cake — center at 60% width on mobile */
          .confetti-variant .b02-cake {
            width: 60vw !important;
            max-width: 360px !important;
          }

          /* Wishlist — single full-width column, twine left, claim right */
          .confetti-variant .b02-wishlist-grid {
            grid-template-columns: 1fr !important;
            gap: 14px !important;
          }
          .confetti-variant .b02-gift {
            transform: none !important;
            padding-top: 0 !important;
          }
          .confetti-variant .b02-gift-row {
            display: flex !important;
            align-items: center !important;
            gap: 12px !important;
            padding: 14px 14px !important;
            clip-path: none !important;
            border-radius: 8px !important;
            text-align: left !important;
          }
          .confetti-variant .b02-gift-row .b02-gift-twine {
            display: block !important;
          }
          .confetti-variant .b02-gift-row .b02-gift-hole {
            display: none !important;
          }
          .confetti-variant .b02-gift-mid {
            flex: 1 !important;
            min-width: 0 !important;
          }
          .confetti-variant .b02-gift-label {
            font-size: 18px !important;
            margin-bottom: 2px !important;
          }
          .confetti-variant .b02-gift-price {
            margin-bottom: 0 !important;
          }
          .confetti-variant .b02-gift-claim {
            min-width: 84px !important;
            min-height: 44px !important;
            padding: 10px 12px !important;
            font-size: 11px !important;
            flex-shrink: 0 !important;
          }
        }
      `}</style>

      {/* ═══════════════════════════════════════════════════════════════
          HERO — confetti drift + big 7 + click-to-celebrate
          ═══════════════════════════════════════════════════════════════ */}
      <section
        ref={heroRef}
        className="b02-hero"
        style={{
          minHeight: "100vh",
          padding: "80px 48px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          ["--b02-ink" as string]: t.ink,
        } as React.CSSProperties}
      >
        {/* Drifting confetti layer (desktop only — hidden ≤768px) */}
        <div
          aria-hidden
          className="b02-mobile-hide-drift"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          {drift.map((d) => (
            <span
              key={d.id}
              className="b02-drift b02-stagger"
              style={{
                position: "absolute",
                left: `${d.left}%`,
                top: `${d.top}%`,
                opacity: 0.7,
                willChange: "transform, opacity",
                animation: reduceMotion
                  ? "none"
                  : `confettiStaggerIn 600ms cubic-bezier(0.34, 1.56, 0.64, 1) ${d.id * 18}ms backwards, confettiDrift${d.variant} ${d.duration}s ease-in-out ${d.delay}s infinite alternate`,
                ["--rot" as string]: `${d.rot}deg`,
                transform: `rotate(${d.rot}deg)`,
              } as React.CSSProperties}
            >
              <ConfettiShapeEl shape={d.shape} size={d.size} color={d.color} />
            </span>
          ))}
        </div>

        {/* Static dense confetti scatter — mobile only, no animation */}
        <div
          aria-hidden
          className="b02-mobile-confetti"
          style={{
            display: "none",
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          {mobileConfetti.map((d) => (
            <span
              key={d.id}
              style={{
                position: "absolute",
                left: `${d.left}%`,
                top: `${d.top}%`,
                opacity: 0.78,
                transform: `rotate(${d.rot}deg)`,
              }}
            >
              <ConfettiShapeEl shape={d.shape} size={d.size} color={d.color} />
            </span>
          ))}
        </div>

        {/* Burst overlay (signature) */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 5,
          }}
        >
          {bursts.map((b) => (
            <div
              key={b.id}
              style={{
                position: "absolute",
                left: b.x,
                top: b.y,
                width: 0,
                height: 0,
              }}
            >
              {b.pieces.map((piece) => (
                <span
                  key={piece.id}
                  style={
                    {
                      position: "absolute",
                      left: 0,
                      top: 0,
                      animation: "burstFly 900ms cubic-bezier(0.16, 1, 0.3, 1) forwards",
                      animationDelay: `${piece.id * 4}ms`,
                      ["--bx"]: `${piece.dx}px`,
                      ["--by"]: `${piece.dy}px`,
                      ["--brot"]: `${piece.rot}deg`,
                      willChange: "transform, opacity",
                      transformOrigin: "center",
                    } as React.CSSProperties
                  }
                >
                  <ConfettiShapeEl
                    shape={piece.shape}
                    size={piece.size}
                    color={piece.color}
                  />
                </span>
              ))}
            </div>
          ))}
        </div>

        {/* Hero content (z-index above confetti) */}
        <div style={{ position: "relative", zIndex: 2 }}>
          <div
            className="b02-hand-callout"
            style={{
              fontFamily: "var(--font-hand)",
              fontSize: 48,
              color: t.accent,
              marginBottom: 16,
              position: "relative",
              display: "inline-block",
            }}
          >
            <span aria-hidden style={{ position: "absolute", left: -24, top: 8, fontSize: 14, color: t.ink }}>✦</span>
            {tt("variant.b02.hero.callout")}
            <span aria-hidden style={{ position: "absolute", right: -24, top: 8, fontSize: 14, color: t.ink }}>✦</span>
          </div>
          <h1
            className="b02-names"
            style={{
              fontFamily: t.font,
              fontSize: "clamp(72px, 12vw, 180px)",
              fontWeight: 400,
              lineHeight: 0.9,
              letterSpacing: "-0.02em",
              marginBottom: 24,
            }}
          >
            {t.names}
          </h1>

          {/* Big "7" centerpiece — Yeseva One numeral with hand-drawn circle */}
          <div
            className="b02-seven-wrap"
            style={{
              position: "relative",
              display: "inline-block",
              margin: "8px auto 28px",
              padding: "14px 28px",
            }}
          >
            <svg
              aria-hidden
              viewBox="0 0 220 220"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            >
              {/* hand-drawn double-circle around the 7 */}
              <path
                d="M 110 22 C 60 22 22 60 22 110 C 22 162 60 198 110 198 C 162 198 198 160 198 110 C 198 58 158 22 110 22"
                stroke={t.accent}
                strokeWidth="2.4"
                fill="none"
                strokeLinecap="round"
              />
              <path
                d="M 110 30 C 66 32 30 66 32 110 C 30 158 66 192 110 190 C 156 192 192 156 190 110 C 192 64 156 30 110 30"
                stroke={t.accent}
                strokeWidth="1.2"
                fill="none"
                opacity="0.45"
                strokeLinecap="round"
              />
            </svg>

            {/* candles on top of the circle */}
            <div
              aria-hidden
              style={{
                position: "absolute",
                left: "50%",
                top: -18,
                transform: "translateX(-50%)",
                display: "flex",
                gap: 6,
              }}
            >
              {[0, 1, 2].map((i) => (
                <Candle key={i} accent={t.accent} ink={t.ink} delay={i * 0.27} />
              ))}
            </div>

            <div
              className="b02-seven"
              style={{
                fontFamily: t.font,
                fontSize: "clamp(120px, 18vw, 200px)",
                lineHeight: 1,
                color: t.accent,
                fontWeight: 400,
                letterSpacing: "-0.04em",
                position: "relative",
                zIndex: 1,
              }}
            >
              7
            </div>

            {/* sparkles around the numeral */}
            <Sparkle style={{ top: "10%", left: "-6%" }} color={t.ink} delay={0} />
            <Sparkle style={{ top: "50%", right: "-10%" }} color={t.accent} delay={0.6} />
            <Sparkle style={{ bottom: "5%", left: "8%" }} color={t.accent} delay={1.1} />
          </div>

          <div
            className="b02-yosh"
            style={{
              fontFamily: t.font,
              fontSize: 30,
              fontStyle: "italic",
              color: t.accent,
              marginBottom: 40,
            }}
          >
            {tt("variant.b02.hero.yosh")}
          </div>

          {/* Photo portrait */}
          <div
            className="b02-hero-portrait"
            style={{
              maxWidth: 320,
              aspectRatio: "1/1",
              margin: "0 auto 32px",
              borderRadius: "50%",
              overflow: "hidden",
              border: `8px solid ${t.accent}`,
              boxShadow: `0 12px 0 -4px ${t.ink}33`,
            }}
          >
            <GuestPhoto
              src={p[0]}
              style={{ width: "100%", height: "100%" }}
              fallbackBg={t.bg}
              fallbackTone={t.accent}
            />
          </div>

          <div
            className="b02-datevenue"
            style={{
              fontFamily: t.font,
              fontSize: 24,
              fontStyle: "italic",
              marginBottom: 32,
            }}
          >
            {t.date} · {t.venue}
          </div>

          {/* Signature: click-to-celebrate / mobile party CTA */}
          <button
            type="button"
            onClick={fireBurst}
            className="b02-cta"
            style={{
              padding: "16px 32px",
              background: t.accent,
              color: "#fff",
              border: `2px solid ${t.ink}`,
              fontFamily: "var(--font-ui)",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.18em",
              cursor: "pointer",
              boxShadow: `5px 6px 0 -1px ${t.ink}`,
              transition: "transform 120ms ease",
              borderRadius: 4,
            }}
            onMouseDown={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = "translate(2px,2px)";
            }}
            onMouseUp={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = "translate(0,0)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = "translate(0,0)";
            }}
          >
            {tt("variant.b02.cta")}
          </button>

          {/* Static party-favor row — mobile-only joy at the bottom of hero */}
          <div className="b02-favors" style={{ display: "none" }} aria-hidden>
            <PartyFavor kind="balloon" accent={t.accent} ink={t.ink} />
            <PartyFavor kind="hat" accent={t.accent} ink={t.ink} />
            <PartyFavor kind="present" accent={t.accent} ink={t.ink} />
            <PartyFavor kind="candle" accent={t.accent} ink={t.ink} />
            <PartyFavor kind="cake" accent={t.accent} ink={t.ink} />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          POLAROID SCATTER — hover physics
          ═══════════════════════════════════════════════════════════════ */}
      {(p[1] || p[2] || p[3]) && (
        <section
          ref={polaroids.ref}
          className="b02-section"
          style={{
            padding: "100px 48px",
            borderTop: `1px solid ${t.ink}22`,
            position: "relative",
            overflow: "hidden",
            ...revealStyle(polaroids.shown),
          }}
        >
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <div
              style={{
                fontFamily: "var(--font-hand)",
                fontSize: 36,
                color: t.accent,
              }}
            >
              {tt("variant.b02.memories.eyebrow")}
            </div>
            <h2
              className="b02-h2"
              style={{
                fontFamily: t.font,
                fontSize: 48,
                fontStyle: "italic",
                fontWeight: 400,
              }}
            >
              {tt("variant.b02.memories.title")}
            </h2>
          </div>
          <div
            className="b02-polaroid-row"
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 40,
              flexWrap: "wrap",
            }}
          >
            {[p[1], p[2], p[3]].filter(Boolean).map((src, i) => {
              // Mobile vertical reel: alternating ±4°. Desktop keeps original tilts.
              const tilt = [-6, 4, -3][i] ?? 0;
              const mobileTilt = i % 2 === 0 ? -4 : 4;
              const captions = [
                tt("variant.b02.memories.caption.0"),
                tt("variant.b02.memories.caption.1"),
                tt("variant.b02.memories.caption.2"),
              ];
              return (
                <Polaroid
                  key={i}
                  src={src}
                  tilt={tilt}
                  mobileTilt={mobileTilt}
                  caption={captions[i] ?? ""}
                  fallbackBg={t.bg}
                  fallbackTone={t.accent}
                  accent={t.accent}
                  onHover={() => play("tap")}
                />
              );
            })}
          </div>
        </section>
      )}

      <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      <GallerySection t={t} />

      {/* ═══════════════════════════════════════════════════════════════
          CAKE + 7 CANDLES MOMENT
          ═══════════════════════════════════════════════════════════════ */}
      <section
        ref={cake.ref}
        className="b02-section"
        style={{
          padding: "100px 48px",
          borderTop: `1px solid ${t.ink}22`,
          textAlign: "center",
          ...revealStyle(cake.shown),
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-hand)",
            fontSize: 36,
            color: t.accent,
          }}
        >
          {tt("variant.b02.cake.eyebrow")}
        </div>
        <h2
          className="b02-h2"
          style={{
            fontFamily: t.font,
            fontSize: 48,
            fontStyle: "italic",
            fontWeight: 400,
            marginBottom: 40,
          }}
        >
          {tt("variant.b02.cake.title")}
        </h2>

        <CakeWith7Candles accent={t.accent} ink={t.ink} bg={t.bg} onHoverCandle={() => play("ding")} />
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          WISHLIST — gift tags with twine + claim button
          ═══════════════════════════════════════════════════════════════ */}
      <section
        ref={wishlist.ref}
        className="b02-section"
        style={{
          padding: "100px 48px",
          borderTop: `1px solid ${t.ink}22`,
          background: `${t.accent}10`,
          ...revealStyle(wishlist.shown),
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <div
            style={{
              fontFamily: "var(--font-hand)",
              fontSize: 36,
              color: t.accent,
            }}
          >
            {tt("variant.b02.wishlist.eyebrow")}
          </div>
          <h2
            className="b02-h2"
            style={{
              fontFamily: t.font,
              fontSize: 48,
              fontStyle: "italic",
              fontWeight: 400,
            }}
          >
            {tt("variant.b02.wishlist.title")}
          </h2>
        </div>
        <div
          className="b02-wishlist-grid"
          style={{
            maxWidth: 760,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: 28,
          }}
        >
          {(
            [
              [tt("variant.b02.wishlist.0.label"), tt("variant.b02.wishlist.0.price")],
              [tt("variant.b02.wishlist.1.label"), tt("variant.b02.wishlist.1.price")],
              [tt("variant.b02.wishlist.2.label"), tt("variant.b02.wishlist.2.price")],
              [tt("variant.b02.wishlist.3.label"), tt("variant.b02.wishlist.3.price")],
            ] as const
          ).map(([n, price], i) => (
            <GiftTag
              key={i}
              label={n}
              price={price}
              accent={t.accent}
              ink={t.ink}
              bg={t.bg}
              tilt={[-3, 2, -1.5, 3][i] ?? 0}
              claimed={!!claimed[i]}
              onClaim={() => claim(i)}
              claimLabel={tt("variant.b02.wishlist.claim")}
              claimedLabel={tt("variant.b02.wishlist.claimed")}
            />
          ))}
        </div>
      </section>

      <RsvpSection
        t={t}
        isDark={isDark}
        rsvp={ctx.rsvp.rsvp}
        setRsvp={wrappedSetRsvp}
        guests={ctx.rsvp.guests}
        setGuests={ctx.rsvp.setGuests}
        guestName={ctx.rsvp.guestName}
        setGuestName={ctx.rsvp.setGuestName}
        label={tt("variant.b02.rsvp.label")}
      />
      <ShareSection t={t} />
      <GuestFooter t={t} />
    </div>
  );
}

// ─── Polaroid with hover physics ───────────────────────────────────────
function Polaroid({
  src,
  tilt,
  mobileTilt,
  caption,
  fallbackBg,
  fallbackTone,
  accent,
  onHover,
}: {
  src?: string;
  tilt: number;
  mobileTilt?: number;
  caption: string;
  fallbackBg: string;
  fallbackTone: string;
  accent: string;
  onHover: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="b02-polaroid"
      onMouseEnter={() => {
        setHover(true);
        onHover();
      }}
      onMouseLeave={() => setHover(false)}
      style={{
        transform: hover
          ? `rotate(${tilt * 0.3}deg) translateY(-8px) scale(1.04)`
          : `rotate(${tilt}deg) translateY(0) scale(1)`,
        padding: "12px 12px 56px",
        background: "#fff",
        boxShadow: hover
          ? "0 22px 48px rgba(0,0,0,0.28)"
          : "0 12px 30px rgba(0,0,0,0.18)",
        width: "min(240px, 80vw)",
        transition:
          "transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 350ms ease",
        cursor: "pointer",
        willChange: "transform",
        ["--b02-mobile-tilt" as string]: `${mobileTilt ?? tilt}deg`,
      } as React.CSSProperties}
    >
      <div style={{ aspectRatio: "1/1", overflow: "hidden", marginBottom: 12 }}>
        <GuestPhoto
          src={src}
          style={{ width: "100%", height: "100%" }}
          fallbackBg={fallbackBg}
          fallbackTone={fallbackTone}
        />
      </div>
      <div
        style={{
          fontFamily: "var(--font-hand)",
          fontSize: 22,
          textAlign: "center",
          color: accent,
        }}
      >
        {caption}
      </div>
    </div>
  );
}

// ─── Single candle (used in big-7 callout) ─────────────────────────────
function Candle({ accent, ink, delay }: { accent: string; ink: string; delay: number }) {
  return (
    <div
      style={{
        width: 8,
        height: 26,
        background: accent,
        position: "relative",
        borderRadius: 1,
        boxShadow: `inset -2px 0 0 0 ${ink}33`,
      }}
    >
      {/* wick */}
      <div
        style={{
          position: "absolute",
          top: -4,
          left: "50%",
          transform: "translateX(-50%)",
          width: 1,
          height: 4,
          background: ink,
        }}
      />
      {/* flame */}
      <div
        className="b02-flame"
        style={{
          position: "absolute",
          top: -14,
          left: "50%",
          transform: "translateX(-50%)",
          width: 7,
          height: 12,
          background:
            "radial-gradient(circle at 50% 70%, #FFE082 0%, #FFB74D 55%, transparent 100%)",
          borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%",
          transformOrigin: "50% 100%",
          animation: `flameFlicker 1.2s ease-in-out ${delay}s infinite`,
          willChange: "transform, opacity",
          filter: "blur(0.3px)",
        }}
      />
    </div>
  );
}

// ─── Sparkle ornament ──────────────────────────────────────────────────
function Sparkle({
  style,
  color,
  delay,
}: {
  style: React.CSSProperties;
  color: string;
  delay: number;
}) {
  return (
    <span
      aria-hidden
      className="b02-sparkle"
      style={{
        position: "absolute",
        ...style,
        animation: `sparkleTwinkle 2.4s ease-in-out ${delay}s infinite`,
        willChange: "transform, opacity",
        pointerEvents: "none",
      }}
    >
      <svg width={18} height={18} viewBox="0 0 18 18">
        <path
          d="M9 0 L10 8 L18 9 L10 10 L9 18 L8 10 L0 9 L8 8 Z"
          fill={color}
        />
      </svg>
    </span>
  );
}

// ─── Cake with 7 lit candles, each on its own flicker delay ────────────
function CakeWith7Candles({
  accent,
  ink,
  bg,
  onHoverCandle,
}: {
  accent: string;
  ink: string;
  bg: string;
  onHoverCandle: () => void;
}) {
  const candleX = [40, 70, 100, 130, 160, 190, 220];
  return (
    <div
      className="b02-cake"
      style={{
        margin: "0 auto",
        width: "min(360px, 90vw)",
        position: "relative",
      }}
    >
      <svg
        viewBox="0 0 260 200"
        style={{ width: "100%", height: "auto", overflow: "visible" }}
      >
        {/* Static flame gradient (terracotta → gold) — used when motion is silenced */}
        <defs>
          <radialGradient id="b02-flame-grad" cx="50%" cy="60%" r="50%">
            <stop offset="0%" stopColor="#FFE082" />
            <stop offset="55%" stopColor="#FFB74D" />
            <stop offset="100%" stopColor={accent} stopOpacity="0.85" />
          </radialGradient>
        </defs>
        {/* cake plate */}
        <ellipse cx="130" cy="186" rx="118" ry="8" fill={ink} opacity="0.18" />
        {/* base tier */}
        <rect
          x="22"
          y="120"
          width="216"
          height="62"
          rx="3"
          fill={bg}
          stroke={accent}
          strokeWidth="1.6"
        />
        {/* base scallop trim */}
        <path
          d="M 22 130
             Q 33 138 44 130 T 66 130 T 88 130 T 110 130 T 132 130 T 154 130 T 176 130 T 198 130 T 220 130 T 238 130"
          stroke={accent}
          strokeWidth="1.2"
          fill="none"
          opacity="0.7"
        />
        {/* top tier */}
        <rect
          x="50"
          y="78"
          width="160"
          height="46"
          rx="3"
          fill={bg}
          stroke={accent}
          strokeWidth="1.6"
        />
        {/* drip */}
        <path
          d="M 50 90
             Q 62 100 74 92 T 100 92 T 126 92 T 152 92 T 178 92 T 204 92 T 210 90"
          stroke={accent}
          strokeWidth="1.4"
          fill={`${accent}22`}
        />
        {/* sprinkles on cake */}
        {Array.from({ length: 14 }).map((_, i) => {
          const x = 60 + ((i * 13) % 140);
          const y = 100 + ((i * 7) % 18);
          const c = ["#7A8268", "#C9A24E", accent, "#8B5C7E"][i % 4];
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={4}
              height={1.6}
              fill={c}
              transform={`rotate(${i * 23} ${x + 2} ${y + 1})`}
            />
          );
        })}
        {/* 7 candles */}
        {candleX.map((x, i) => (
          <g
            key={i}
            transform={`translate(${x - 4}, 50)`}
            onMouseEnter={onHoverCandle}
            style={{ cursor: "pointer" }}
          >
            {/* candle body — alternating colors */}
            <rect
              x="0"
              y="0"
              width="8"
              height="30"
              fill={i % 2 === 0 ? accent : "#7A8268"}
              rx="1"
            />
            {/* stripes */}
            <line x1="0" y1="8" x2="8" y2="8" stroke="#fff" strokeWidth="1" opacity="0.7" />
            <line x1="0" y1="18" x2="8" y2="18" stroke="#fff" strokeWidth="1" opacity="0.7" />
            {/* wick */}
            <line x1="4" y1="-4" x2="4" y2="0" stroke={ink} strokeWidth="1" />
            {/* flame — gradient gives a lively look even when flicker is off */}
            <ellipse
              cx="4"
              cy="-9"
              rx="3"
              ry="6"
              fill="url(#b02-flame-grad)"
              className="b02-flame"
              style={{
                transformOrigin: "4px -3px",
                animation: `flameFlicker 1.${(i * 17) % 9}s ease-in-out ${(i * 0.13) % 1}s infinite`,
              }}
            />
            <ellipse cx="4" cy="-7" rx="1.6" ry="3.2" fill="#FFE082" opacity="0.9" />
          </g>
        ))}
      </svg>
    </div>
  );
}

// ─── Static party favors (mobile-only row above scroll) ───────────────
// Five small SVG icons in muted candy colors. Static, decorative — they
// give the kid-birthday hero a final visual pop now that mobile motion
// is silenced.
function PartyFavor({
  kind,
  accent,
  ink,
}: {
  kind: "balloon" | "hat" | "present" | "candle" | "cake";
  accent: string;
  ink: string;
}) {
  const candy = {
    pink: "#E8A1B5",
    sage: "#A6B59A",
    butter: "#E6C877",
    plum: "#B493B0",
    sky: "#9BB7C4",
  };
  const stroke = ink;
  const sw = 1.2;
  switch (kind) {
    case "balloon":
      return (
        <svg width="34" height="48" viewBox="0 0 34 48" aria-hidden>
          <ellipse cx="17" cy="18" rx="13" ry="16" fill={candy.pink} stroke={stroke} strokeWidth={sw} />
          <path d="M 14 32 L 17 36 L 20 32 Z" fill={candy.pink} stroke={stroke} strokeWidth={sw} />
          <path d="M 17 36 Q 14 40 17 44 Q 20 46 17 48" stroke={stroke} strokeWidth={sw} fill="none" />
          <ellipse cx="13" cy="14" rx="2" ry="3" fill="#fff" opacity="0.55" />
        </svg>
      );
    case "hat":
      return (
        <svg width="36" height="44" viewBox="0 0 36 44" aria-hidden>
          <path d="M 18 4 L 30 38 L 6 38 Z" fill={candy.sage} stroke={stroke} strokeWidth={sw} />
          <line x1="6" y1="38" x2="30" y2="38" stroke={stroke} strokeWidth={sw} />
          <circle cx="12" cy="22" r="2" fill={candy.butter} />
          <circle cx="22" cy="28" r="2" fill={candy.pink} />
          <circle cx="18" cy="4" r="3" fill={candy.butter} stroke={stroke} strokeWidth={sw} />
        </svg>
      );
    case "present":
      return (
        <svg width="38" height="40" viewBox="0 0 38 40" aria-hidden>
          <rect x="4" y="14" width="30" height="22" fill={candy.plum} stroke={stroke} strokeWidth={sw} />
          <rect x="4" y="10" width="30" height="6" fill={candy.butter} stroke={stroke} strokeWidth={sw} />
          <rect x="17" y="10" width="4" height="26" fill={candy.butter} stroke={stroke} strokeWidth={sw} />
          <path d="M 19 10 Q 12 4 14 10 Q 14 4 19 10 Q 26 4 24 10 Q 24 4 19 10 Z" fill={candy.butter} stroke={stroke} strokeWidth={sw} />
        </svg>
      );
    case "candle":
      return (
        <svg width="22" height="48" viewBox="0 0 22 48" aria-hidden>
          <rect x="6" y="14" width="10" height="30" fill={candy.sky} stroke={stroke} strokeWidth={sw} rx="1" />
          <line x1="6" y1="22" x2="16" y2="22" stroke="#fff" strokeWidth="1.2" />
          <line x1="6" y1="32" x2="16" y2="32" stroke="#fff" strokeWidth="1.2" />
          <line x1="11" y1="10" x2="11" y2="14" stroke={stroke} strokeWidth="1" />
          <ellipse cx="11" cy="6" rx="3" ry="6" fill={accent} opacity="0.85" />
          <ellipse cx="11" cy="7" rx="1.4" ry="3" fill="#FFE082" />
        </svg>
      );
    case "cake":
      return (
        <svg width="38" height="40" viewBox="0 0 38 40" aria-hidden>
          <path d="M 4 36 L 19 6 L 34 36 Z" fill={candy.butter} stroke={stroke} strokeWidth={sw} />
          <path d="M 10 24 L 28 24" stroke={candy.pink} strokeWidth="2" />
          <path d="M 7 30 L 31 30" stroke={candy.sage} strokeWidth="2" />
          <circle cx="14" cy="28" r="1.4" fill={accent} />
          <circle cx="22" cy="20" r="1.4" fill={candy.plum} />
        </svg>
      );
  }
}

// ─── Gift tag with twine + claim button ────────────────────────────────
function GiftTag({
  label,
  price,
  accent,
  ink,
  bg,
  tilt,
  claimed,
  onClaim,
  claimLabel,
  claimedLabel,
}: {
  label: string;
  price: string;
  accent: string;
  ink: string;
  bg: string;
  tilt: number;
  claimed: boolean;
  onClaim: () => void;
  claimLabel: string;
  claimedLabel: string;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="b02-gift"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        transform: hover
          ? `rotate(${tilt * 0.3}deg) translateY(-4px)`
          : `rotate(${tilt}deg) translateY(0)`,
        transition: "transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        paddingTop: 28,
        willChange: "transform",
      }}
    >
      {/* twine string going up off the tag (desktop) */}
      <svg
        aria-hidden
        viewBox="0 0 60 40"
        style={{
          position: "absolute",
          top: -8,
          left: "50%",
          transform: "translateX(-50%)",
          width: 60,
          height: 40,
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        <path
          d="M 30 38 Q 18 18 30 8 Q 42 -2 30 -10"
          stroke={ink}
          strokeWidth="1.2"
          fill="none"
          opacity="0.8"
        />
      </svg>
      {/* tag body — clipped corner via clip-path on desktop, full row on mobile */}
      <div
        className="b02-gift-row"
        style={{
          background: bg,
          border: `1.5px solid ${accent}`,
          padding: "32px 20px 20px",
          textAlign: "center",
          position: "relative",
          clipPath:
            "polygon(0% 16%, 14% 0%, 100% 0%, 100% 100%, 0% 100%)",
          boxShadow: hover
            ? `4px 5px 0 -1px ${accent}66`
            : `2px 3px 0 -1px ${accent}33`,
          transition: "box-shadow 350ms ease",
        }}
      >
        {/* string hole — desktop */}
        <div
          aria-hidden
          className="b02-gift-hole"
          style={{
            position: "absolute",
            top: 12,
            left: 18,
            width: 9,
            height: 9,
            borderRadius: "50%",
            background: ink,
            boxShadow: `inset 0 0 0 1.5px ${accent}`,
          }}
        />
        {/* twine knot — mobile only, sits on the left of the row */}
        <span
          aria-hidden
          className="b02-gift-twine"
          style={{ display: "none", flexShrink: 0 }}
        >
          <svg width="22" height="36" viewBox="0 0 22 36">
            <path
              d="M 11 2 Q 4 12 11 20 Q 18 28 11 34"
              stroke={ink}
              strokeWidth="1.4"
              fill="none"
              opacity="0.85"
            />
            <circle cx="11" cy="20" r="3" fill={accent} stroke={ink} strokeWidth="0.8" />
          </svg>
        </span>
        <div className="b02-gift-mid">
          <div
            className="b02-gift-label"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 20,
              fontStyle: "italic",
              marginBottom: 8,
              marginTop: 4,
              color: ink,
              opacity: claimed ? 0.55 : 1,
              textDecoration: claimed ? "line-through" : "none",
            }}
          >
            {label}
          </div>
          <div
            className="b02-gift-price"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              color: accent,
              letterSpacing: "0.08em",
              marginBottom: 14,
            }}
          >
            {price}
          </div>
        </div>
        <button
          type="button"
          onClick={onClaim}
          disabled={claimed}
          className="b02-gift-claim"
          style={{
            padding: "8px 14px",
            background: claimed ? "transparent" : accent,
            color: claimed ? accent : "#fff",
            border: `1.5px solid ${accent}`,
            fontFamily: "var(--font-ui)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.16em",
            cursor: claimed ? "default" : "pointer",
            borderRadius: 3,
            transition: "all 200ms ease",
          }}
        >
          {claimed ? claimedLabel : claimLabel}
        </button>
      </div>
    </div>
  );
}
